import TenantComponents from '@components';
import tenantData from '@data';
import tenantRoutes from '@routes';
import tenantTheme from '@theme';
import { Col, Divider, Popover, Row, Space, Typography } from 'antd';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import tenantConstants from '@constants';
import { postListingClickEvent } from '../../../../services/analyticsService';
import {
  Button,
  Card,
  ConfirmationModal,
  Flex,
  Group,
  Heading,
  Icon,
  NationalDayGift,
  Skeleton,
  Switch,
  notification,
} from '../../../../components/common';
import OtpVerificationModal from '../../../../components/otp-verification-modal/otp-verification-modal';
import { PERMISSIONS_TYPE } from '../../../../constants/permissions';
import { usePageTitle, useRouteNavigate, useScrollToFieldForm } from '../../../../hooks';
import RegaCard from './RegaCard';
import SuccessfulPaymentModal from '../../../../components/checkout/successfulPaymentModal';
import { PostListingForm } from '../../../../components/post-listing';
import { getAppSource } from '../../../../store/parentApi';
import { setUsersList } from '../../../../store/appSlice';
import { useLazyGetLocationsBasedOnNationalQuery, useUpdateListingMutation } from '../../../../apis/postlisting';
import { useLazyGetApplicableProductsQuery } from '../../../../apis/listings';
import ProjectHeaderCard from './projectHeaderCard';
import RenderPostListingFields from '../../../common/components/post-listing/RenderPostListingFIelds';
import { PostListingAction } from '../../../common/components/post-listing/styled';
import NafathVerificationModal from '../../../../components/nafath-verification-modal/nafath-verification-modal';
import LimitNonSaudiNationalModal from '../../../../components/limitNonSaudiNationalModal/LimitNonSaudiNationalModal';
import { isKeycloakDisabledForPath } from '../../../../utility/helpers';
import { useNationalDay } from '@/hooks';

const PostListing = (props) => {
  const { Text } = Typography;
  const { isNationalDayActive } = useNationalDay();
  const { t } = useTranslation();
  usePageTitle(t('Post Listing - Profolio'));

  const { formData = {}, loading, isMobile, handleMagicAdPost } = props;
  const [getApplicableProducts, {}] = useLazyGetApplicableProductsQuery();

  const [autoRenew, setAutoRenew] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [fetchContentLoading, setFetchContentLoading] = useState(false);
  const [applicableProduct, setApplicableProduct] = useState(null);
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  // const [successModalData, setSuccessModalData] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const { user } = useSelector((state) => state.app.loginUser);

  const users = useSelector((state) => state.app.userGroup.list);
  const { locale } = useSelector((state) => state?.app?.AppConfig);
  const isDailyRentalListing = formData?.listing?.listing_purpose?.slug == 'daily-rental';
  const isOffPlan = formData?.listing?.is_offplan_listing;
  const completion_status = formData?.listing?.completion_status;
  const isProjectMixed = formData?.listing?.location?.classification === 'mixed';
  const deductionRef = useRef();
  const formikRef = useRef();
  const autoRenewModalRef = useRef();
  const otpVerifyRef = useRef();
  const dailyRentalBathroomsList = [{ key: '0', label: '0' }]?.concat(tenantData.bathroomsList);
  const [updateListing] = useUpdateListingMutation();
  const [getLocationsBasedOnNational] = useLazyGetLocationsBasedOnNationalQuery();
  const [autoGenerateContent, setAutoGenerateContent] = useState({
    title: false,
    description: false,
  });
  const limitModalRef = useRef();
  const navigate = useRouteNavigate();
  const dispatch = useDispatch();

  const onSubmit = async () => {
    if (user?.is_nafaz_verified && !user?.is_saudi_national) {
      limitModalRef.current.open();
      return;
    }
    if (formik?.values?.property_images?.some((e) => !!e?.uploading && !e?.inError)) {
      notification.error(t('Image uploading is still in progress'));
    } else {
      if (!formData?.listing?.is_posted && !user?.is_mobile_verified) {
        setSubmitLoading(true);
        otpVerifyRef.current.onVerify(formik?.values['mobile']);
      } else {
        handlePostOrUpdateListing();
      }
    }
  };
  const formik = useFormik({
    initialValues: isDailyRentalListing
      ? tenantData.getRentalInitialValues(formData?.listing, user)
      : isOffPlan
        ? tenantData.getOffPlanInitialValues(formData?.listing, user)
        : tenantData.getPostListingInitialValues(formData?.listing, user),
    validationSchema: tenantData.getPostListingValidationSchema(
      locale,
      formData?.listing?.listing_purpose?.slug,
      formData?.listing?.listing_type?.id,
      user?.is_mobile_verified,
      formData?.listing?.is_offplan_listing,
      user?.domain_name,
      autoGenerateContent.title,
      autoGenerateContent.description,
    ),
    validateOnChange: false,
    onSubmit: onSubmit,
    innerRef: formikRef,
  });

  const resetValuesOnPropertyTypeChange = () => {
    formik.setFieldValue('price', '', false);
    formik.setFieldValue('area', '', false);
    formik.setFieldValue('feature_and_amenities', null, false);
    formik.setFieldValue(locale == 'en' ? 'property_title_en' : 'property_title_ar', '', false);
    formik.setFieldValue(locale == 'en' ? 'property_description_en' : 'property_description_ar', '', false);
  };

  const bedroomList = useMemo(() => {
    return tenantData.skipFieldsForField?.['bedrooms'].property_type[
      formik.values['sub_listing_type']?.id || formik.values['property_type']
    ]?.studio
      ? tenantData.bedroomsList?.filter((item) => item.key !== '-1')
      : tenantData.bedroomsList;
  }, [
    tenantData.skipFieldsForField,
    formik.values['property_type'],
    tenantData.bedroomsList,
    formik.values['sub_listing_type']?.id,
  ]);

  useEffect(() => {
    if (
      formData?.listing?.id &&
      !formData?.listing?.isPosted &&
      formik?.values['location-info']?.city?.id &&
      user?.credits?.ksa
    ) {
      getApplicableProductsData();
    }
  }, [formik?.values['location-info']?.city?.id, user?.credits?.ksa]);

  const getLocations = async (nationalAddress) => {
    setLocationLoading(true);
    const locationsResponse = await getLocationsBasedOnNational(nationalAddress);
    if (locationsResponse) {
      setLocationLoading(false);
      if (locationsResponse?.error) {
        notification.error(locationsResponse?.error);
      } else if (locationsResponse?.data?.response?.geometry) {
        formik?.setFieldValue(
          'location-info',
          {
            ...formik.values['location-info'],
            map: {
              longitude: locationsResponse?.data?.response?.geometry?.location?.lng,
              latitude: locationsResponse?.data?.response?.geometry?.location?.lat,
              type: 'map',
            },
          },
          true,
        );
      }
    }
  };

  const getApplicableProductsData = async () => {
    const response = await getApplicableProducts({
      requestParams: {
        listing_id: formData?.listing?.id,
        location_id: isDailyRentalListing && formik?.values['location-info']?.city?.id,
      },
      platforms: user?.platforms,
      upSell: false,
      listingDetailResponse: null,
    });
    if (response) {
      if (response.error) {
        notification.error(response.error);
      } else {
        const basicListing = response?.data?.products?.find((p) => p.slug === 'basic-listing');
        setCreditsInfo({
          ...creditsInfo,
          productId: basicListing?.product_id,
          requiredCredits: basicListing?.credits_required?.[30],
          isSufficient: !!(user?.credits?.ksa?.available >= basicListing?.credits_required?.[30]),
        });
      }
    }
  };

  const fetchRequiredQuota = async () => {
    const response = await getApplicableProducts({
      requestParams: {
        listing_id: formData?.listing?.id,
      },
      productSlug: tenantData.getListingActions('basic-listing')?.slug,
    });

    if (response) {
      if (response?.error) {
        notification.error(response?.error);
      } else {
        setApplicableProduct(response);
        deductionRef?.current && deductionRef?.current?.showQuotaCreditModal();
      }
    }
  };
  const onSaveChanges = async () => {
    setSaveLoading(true);
    const res = await updateListing({
      listingId: formik?.values?.id,
      values: formik?.values,
      listing: formData?.listing,
      autoRenew,
      source: getAppSource(),
    });
    setSaveLoading(false);

    if (res?.error) {
      notification.error(t(res?.error));
    } else {
      notification.success(t('Changes saved successfully'));
    }
  };

  const onUpdateListing = async () => {
    setModalLoading(true);
    const res = await updateListing({
      listingId: formik?.values?.id,
      values: formik?.values,
      listing: formData?.listing,
      autoRenew,
      source: getAppSource(),
      productId: creditsInfo?.isSufficient ? creditsInfo?.productId : null,
    });
    if (res) {
      setSubmitLoading(false);
      setModalLoading(false);
      deductionRef?.current && deductionRef?.current?.hideQuotaCreditModal();
      if (res?.error) {
        notification.error(res?.error);
      } else {
        postListingClickEvent(user, res, formik?.values, autoRenew, formData?.listing?.is_posted);

        if (typeof handleMagicAdPost === 'function') {
          handleMagicAdPost(res);
          return;
        }

        if (!formData?.listing?.is_posted && !!user?.isCurrencyUser) {
          if (res?.data?.listing?.disposition?.slug == 'pending-nafaz-verification') {
            setShowVerificationModal(true);
            // setSuccessModalData({ listing_id: formData?.listing?.id });
          } else {
            navigate(`${tenantRoutes.app().post_listing.path}/${formData?.listing?.id}/upgrade`, {
              state: { listing: formData?.listing, listingDispositionSlug: res?.data?.listing?.disposition?.slug },
            });
          }
        } else {
          notification?.success(
            `${t('Listing')} ${formData?.listing?.is_posted ? t('Updated') : t('Posted')} ${t('Successfully!')}`,
          );
          navigate(`${tenantRoutes.app().listings.path}`);
        }
      }
    }
  };

  const handlePostOrUpdateListing = () => {
    setSubmitLoading(true);
    if (!formData?.listing?.is_posted) {
      if (!user?.isCurrencyUser) {
        fetchRequiredQuota();
      } else {
        onUpdateListing();
      }
    } else {
      onUpdateListing();
    }
  };

  const setUserUpdatedValues = () => {
    formik &&
      formData.listing &&
      formik.setValues(
        isDailyRentalListing
          ? tenantData.getRentalInitialValues(formData.listing, user)
          : isOffPlan
            ? tenantData.getOffPlanInitialValues(formData.listing, user)
            : tenantData.getPostListingInitialValues(formData.listing, user),
      );
  };

  const conditionalUser = handleMagicAdPost ? user : null

  useEffect(() => {
    //ToDo as discussed
    user?.permissions?.[PERMISSIONS_TYPE.LISTINGS] && !users?.length && setUsersList();
  }, [user, formik?.values?.posting_as]);

  useEffect(() => {
    setUserUpdatedValues();
  }, [formData.listing, conditionalUser]);

  useScrollToFieldForm(formik);
  const offPlanProps = {
    furnished: true,
    saleType: true,
    additionalNumber: true,
    saleTypes: formData?.listing?.saleTypes,
    isOffPlan,
    completion_status,
  };

  const dailyRentalProps = {
    nationalAddress: true,
    mobileVerification: true,
  };
  const autoGenerate = {
    autoGenerateContent,
    setAutoGenerateContent,
  };
  const renderAutoRenew = () => {
    return (
      <Card
        className="w-100"
        style={{ backgroundColor: '#F7F7F7', maxWidth: !isMobile && 'max-content' }}
        bodyStyle={{ padding: 8 }}
      >
        <Flex gap="10px" align="center" justify={isMobile && 'space-between'}>
          <Flex align="center" gap="4px">
            <Text type="secondary" className={isMobile && 'fz-12'}>
              {t('Auto-Renew')}
            </Text>
            <Popover content={<>{t('The listing will be automatically renewed after 30 days')}</>} action="hover">
              <>
                <Icon
                  style={{ marginTop: isMobile && '2px' }}
                  icon="AiOutlineInfoCircle"
                  size={12}
                  color={tenantTheme.gray700}
                />
              </>
            </Popover>
          </Flex>

          <Switch
            value={autoRenew}
            size="small"
            onChange={() => {
              autoRenew ? autoRenewModalRef?.current?.showModal() : setAutoRenew(true);
            }}
          />
        </Flex>

        <ConfirmationModal
          title={t('Disable auto renew')}
          ref={autoRenewModalRef}
          onCancel={() => autoRenewModalRef?.current?.hideModal()}
          onSuccess={() => {
            setAutoRenew(!autoRenew);
            autoRenewModalRef?.current?.hideModal();
          }}
        >
          {t('Are you sure you want to disable auto renew for this listing')}
        </ConfirmationModal>
      </Card>
    );
  };

  const setTitleDescription = () => {
    formik.setFieldValue('property_title_en', formik?.values?.title, false);
    formik.setFieldValue('property_title_ar', formik?.values?.title_l1, false);
    formik.setFieldValue('property_description_en', formik?.values?.description, false);
    formik.setFieldValue('property_description_ar', formik?.values?.description_l1, false);
  };

  return !!loading ? (
    <PostListingSkeleton />
  ) : (
    <>
      <TenantComponents.QuotaCreditModal
        ref={deductionRef}
        action={applicableProduct}
        onSuccess={onUpdateListing}
        loading={modalLoading}
        onCancel={() => {
          deductionRef?.current && deductionRef?.current?.hideQuotaCreditModal();
          setSubmitLoading(false);
        }}
      />
      <Group gap={isMobile ? '0px' : '8px'}>
        {!isMobile && !formData?.listing?.is_posted && (
          <div>
            <Heading as="h2" className="fs20 mb-4 ">
              {isDailyRentalListing ? t('Post a Daily Rental Listing') : t('Post a Listing')}
            </Heading>
            <Typography.Paragraph className="color-gray-dark mb-0">
              {isDailyRentalListing
                ? t('Reach thousands of guests in a few simple step')
                : t('Reach thousand of buyers and tenants in a few steps')}
            </Typography.Paragraph>
          </div>
        )}
        {isOffPlan && <ProjectHeaderCard project={formData?.listing?.project} />}
        {!isDailyRentalListing && <RegaCard listing={formData?.listing} />}
        <Card
          bodyStyle={{
            padding: isMobile ? '0 0 20px' : '40px 0',
          }}
        >
          <form className="form-post-listing">
            <Group gap={isMobile ? '12px' : '32px'}>
              {isDailyRentalListing || isOffPlan ? (
                <PostListingForm
                  formik={formik}
                  isMobile={isMobile}
                  loading={loading}
                  users={users}
                  setLoading={setFetchContentLoading}
                  listingPurposeId={formData?.listing?.listing_purpose?.id}
                  isDailyRental={isDailyRentalListing}
                  areaMaxLength={4}
                  tooltipText={t(
                    'The National Address, developed by Saudi Post, is a uniform address format designed to standardize address information across Saudi Arabia.',
                  )}
                  listingType={t('Per Night')}
                  bedroomList={bedroomList}
                  bathRoomsList={dailyRentalBathroomsList}
                  isCreditsAvailable={user?.credits?.ksa?.available > 0}
                  clearUserSelections={resetValuesOnPropertyTypeChange}
                  getLocationsLatLong={getLocations}
                  nationalAddressLoading={locationLoading}
                  alreadyPosted={formData?.listing?.is_posted}
                  areaField={true}
                  isProjectMixed={isProjectMixed}
                  {...(isOffPlan && offPlanProps)}
                  {...(isDailyRentalListing && dailyRentalProps)}
                  {...autoGenerate}
                />
              ) : (
                <RenderPostListingFields
                  formik={formik}
                  isMobile={isMobile}
                  loading={loading}
                  users={users}
                  setLoading={setFetchContentLoading}
                  alreadyPosted={formData?.listing?.is_posted}
                  {...autoGenerate}
                />
              )}
              {creditsInfo?.isSufficient && !tenantConstants?.HIDE_AUTO_RENEWAL && (
                <div style={{ paddingInline: '16px' }}>{isMobile && renderAutoRenew()}</div>
              )}
            </Group>
          </form>

          <Divider />

          <PostListingAction style={{ paddingInline: isMobile ? '16px' : '64px', alignItems: 'center' }}>
            {creditsInfo?.isSufficient && !tenantConstants?.HIDE_AUTO_RENEWAL && <>{!isMobile && renderAutoRenew()}</>}
            <Flex
              gap={isMobile ? '20px' : '60px'}
              align="center"
              justify={isMobile ? 'space-between' : 'end'}
              className={'w-100'}
              style={{ marginInlineStart: 'auto' }}
              disabled={isDailyRentalListing && !creditsInfo}
            >
              {creditsInfo?.isSufficient && !isKeycloakDisabledForPath() && (
                <Flex gap={'8px'}>
                  <NationalDayGift />
                  {(!isNationalDayActive || !isMobile) && (
                    <div>
                      <Text type="secondary" className="d-block">
                        {t('Required')}
                      </Text>
                      <Text className="fw-700">
                        {' '}
                        {creditsInfo?.requiredCredits} <span className="fw-400">{t('Credits')}</span>
                      </Text>
                    </div>
                  )}
                </Flex>
              )}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 20,
                }}
              >
                {!formData?.listing?.is_posted && !isKeycloakDisabledForPath() && (
                  <Button
                    type="default"
                    size={isMobile ? 'medium' : 'large'}
                    onClick={onSaveChanges}
                    loading={saveLoading}
                    style={{ width: isMobile ? 100 : 140 }}
                  >
                    {t('Save Changes')}
                  </Button>
                )}

                <Button
                  style={{ width: isMobile ? 100 : 140 }}
                  type="primary"
                  size={isMobile ? 'medium' : 'large'}
                  onClick={formik.handleSubmit}
                  loading={submitLoading}
                  disabled={fetchContentLoading}
                >
                  {formData?.listing?.is_posted
                    ? t('Update')
                    : user?.is_mobile_verified
                      ? creditsInfo?.isSufficient
                        ? t('Post Listing')
                        : t('Continue')
                      : t('Verify & Submit')}
                </Button>
              </div>
            </Flex>
          </PostListingAction>
        </Card>
      </Group>
      <NafathVerificationModal
        listingId={formData?.listing?.id}
        setVisible={setShowVerificationModal}
        visible={showVerificationModal}
        actionSource="upgrade"
      />
      {/* <SuccessfulPaymentModal data={successModalData} setData={setSuccessModalData} /> */}
      <LimitNonSaudiNationalModal ref={limitModalRef} actionSource="upgrade" clickTrigger={true} />
      <OtpVerificationModal
        ref={otpVerifyRef}
        value={user?.mobile}
        onSuccess={handlePostOrUpdateListing}
        onCancelModal={() => setSubmitLoading(false)}
        onFail={() => setSubmitLoading(false)}
      />
    </>
  );
};

const PostListingSkeleton = () => {
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  const renderFields = () => {
    return [1, 2, 3, 4, 5].map((e) => {
      return (
        <Col xs={{ span: 24 }} key={e}>
          <Group className="mb-16" template="auto 1fr" gap="16px">
            <Skeleton type="avatar" />
            <Space direction="vertical">
              <Skeleton block style={{ height: 26, maxWidth: '20%' }} />
              <Skeleton type="input" block />
            </Space>
          </Group>
        </Col>
      );
    });
  };

  const renderRega = () => {
    return (
      <>
        {isMobile ? (
          <Group template="initial" gap="0px">
            <Skeleton type="paragraph" paragraph={{ rows: 3 }} />
          </Group>
        ) : (
          <Group template="repeat(3,minmax(100px, 250px))" gap="40px" className="justify-content-between">
            <Skeleton type="paragraph" paragraph={{ rows: 2 }} />
            <Skeleton type="paragraph" paragraph={{ rows: 2 }} />
            <Skeleton type="paragraph" paragraph={{ rows: 2 }} />
          </Group>
        )}
      </>
    );
  };

  return (
    <Group gap="16px">
      <Card bodyStyle={{ padding: 30 }}>{renderRega()}</Card>

      <Card bodyStyle={{ padding: 30 }}>
        {/* <Row align="center" className="mb-40" style={{ gap: 16 }}>
          <Skeleton type="avatar" />
          <Skeleton type="input" style={{ height: 2, marginTop: 12 }} />
          <Skeleton type="avatar" />
        </Row> */}
        <Row align="center" style={{ gap: 16 }}>
          <Col xs={{ span: 24, offset: 0 }} lg={{ span: 18, offset: 6 }}>
            <Group style={{ maxWidth: 640 }}>{renderFields()}</Group>
          </Col>
        </Row>
      </Card>

      {/* {[1].map(item => (
        <CardBlock loading key={item}>
          <Group style={{ maxWidth: 640 }}>{renderFields()}</Group>
        </CardBlock>
      ))} */}
    </Group>
  );
};

export default PostListing;
